import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faUsers, faMapMarkerAlt, faLock, faShieldHalved,
  faPencilAlt, faPlus, faTrash, faEnvelope, faPhone,
  faCalendarDays, faIdCard, faCamera, faUserPlus,
  faHome, faBuilding, faLocationDot, faKey, faBell,
  faCheckCircle, faXmark, faCircleInfo, faPaperPlane,
  faEyeSlash, faFolderOpen, faLink, faDownload, faUserSlash, faFileContract, faArrowUpRightFromSquare,
  faGlobe, faLocationCrosshairs,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Container } from 'react-bootstrap';
import { AxiosError } from 'axios';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import { Dependant, DependantFormData, RelationshipType } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../../i18n/i18n';
import { useProfile } from '../../context/ProfileContext';
import './Profile.css';
import BackButton from '../../components/BackButton/BackButton';

// Toast notification component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

// Confirmation dialog component
interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  confirmColor, onConfirm, onCancel,
}) => {
  return (
    <div className="pf-dialog-overlay" onClick={onCancel}>
      <div className="pf-dialog" onClick={e => e.stopPropagation()}>
        <h3 className="pf-dialog-title">{title}</h3>
        <p className="pf-dialog-message">{message}</p>
        <div className="pf-dialog-buttons">
          <button className="pf-btn pf-btn-cancel" onClick={onCancel}>{cancelLabel}</button>
          <button
            className="pf-btn pf-btn-save"
            style={confirmColor ? { background: confirmColor } : undefined}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap = { success: faCheckCircle, error: faXmark, info: faCircleInfo };

  return (
    <div className={`pf-toast pf-toast-${type}`}>
      <span className="pf-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="pf-toast-message">{message}</span>
      <button className="pf-toast-close" onClick={onClose}>
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
};

interface StatItem {
  icon: IconDefinition;
  color: string;
  value: string | number;
  label: string;
}

type ProfileTab = 'personal' | 'address' | 'security' | 'family' | 'privacy' | 'language';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, i18n } = useTranslation(['profile', 'common']);
  const { isDependantUser, fetchProfiles, activeProfile, switchToSelf, setProfilePhoto } = useProfile();
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const initialTab = ((location.state as { tab?: ProfileTab } | null)?.tab) || 'personal';
  const [activeTab, setActiveTab] = useState<ProfileTab>(initialTab);

  useEffect(() => {
    const requestedTab = (location.state as { tab?: ProfileTab } | null)?.tab;
    if (requestedTab) setActiveTab(requestedTab);
  }, [location.state]);
  const [addressType, setAddressType] = useState('home');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasLoadedInitialDataRef = useRef(false);

  // Profile data state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    mobile_number: '',
    member_id: '',
    employee_id: '',
    role: '',
    corporate_info: null as { id: number; name: string; code: string } | null,
    profile: {
      personal_email: '',
      gender: '',
      dob: '',
      marital_status: '',
      blood_group: '',
      corporate_name: '',
    },
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);

  // Aggregate counts for the stats row (family_members, addresses, reminders)
  const [summaryCounts, setSummaryCounts] = useState({
    family_members_count: 0,
    addresses_count: 0,
    reminders_count: 0,
  });

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  }, []);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    confirmColor?: string;
    onConfirm: () => void;
  } | null>(null);

  const showConfirm = useCallback((opts: {
    title: string;
    message: string;
    confirmLabel?: string;
    confirmColor?: string;
    onConfirm: () => void;
  }) => {
    setConfirmDialog(opts);
  }, []);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setProfileImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Auto-save the photo to server right away
    try {
      const formData = new FormData();
      formData.append('name', profileData.name);
      formData.append('profile.profile_photo', file);
      const res = await axiosInstance.put(API_ENDPOINTS.auth.profile, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfilePhoto(res.data?.profile?.profile_photo || null);
      setProfileImageFile(null);
      showToast('Profile photo updated!', 'success');
    } catch {
      showToast('Failed to upload profile photo', 'error');
      setProfileImage(null);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  // Fetch profile data on mount
  const fetchProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const res = await axiosInstance.get(API_ENDPOINTS.auth.profile);
      const data = res.data;
      setProfileData({
        name: data.name || '',
        email: data.email || '',
        mobile_number: data.mobile_number || '',
        member_id: data.member_id || '',
        employee_id: data.employee_id || '',
        role: data.role || '',
        corporate_info: data.corporate_info || null,
        profile: {
          personal_email: data.profile?.personal_email || '',
          gender: data.profile?.gender || '',
          dob: data.profile?.dob || '',
          marital_status: data.profile?.marital_status || '',
          blood_group: data.profile?.blood_group || '',
          corporate_name: data.profile?.corporate_name || '',
        },
      });
      if (data.profile?.profile_photo) {
        setProfileImage(data.profile.profile_photo);
      }
      setProfilePhoto(data.profile?.profile_photo || null);
    } catch {
      showToast('Failed to load profile data', 'error');
    } finally {
      setProfileLoading(false);
    }
  }, [showToast, setProfilePhoto]);

  const fetchSummaryCounts = useCallback(async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.auth.profileSummary);
      setSummaryCounts({
        family_members_count: res.data?.family_members_count ?? 0,
        addresses_count: res.data?.addresses_count ?? 0,
        reminders_count: res.data?.reminders_count ?? 0,
      });
    } catch {
      // Silent fail — stats will show zeros
    }
  }, []);

  useEffect(() => {
    if (hasLoadedInitialDataRef.current) {
      return;
    }

    hasLoadedInitialDataRef.current = true;
    fetchProfile();
    fetchSummaryCounts();
  }, [fetchProfile, fetchSummaryCounts]);

  const handleProfileChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setProfileData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleProfileNestedChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setProfileData(prev => ({
      ...prev,
      profile: { ...prev.profile, [field]: e.target.value },
    }));
  };

  const handleSaveProfile = async () => {
    if (!profileData.name.trim()) {
      showToast('Full name is required', 'error');
      return;
    }
    try {
      setProfileSaving(true);

      if (profileImageFile) {
        // Use FormData (multipart/form-data) when there is an image to upload
        const formData = new FormData();
        formData.append('name', profileData.name);
        formData.append('mobile_number', profileData.mobile_number);
        formData.append('profile.personal_email', profileData.profile.personal_email);
        formData.append('profile.gender', profileData.profile.gender);
        formData.append('profile.dob', profileData.profile.dob || '');
        formData.append('profile.marital_status', profileData.profile.marital_status);
        formData.append('profile.blood_group', profileData.profile.blood_group);
        formData.append('profile.profile_photo', profileImageFile);

        const res = await axiosInstance.put(API_ENDPOINTS.auth.profile, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setProfilePhoto(res.data?.profile?.profile_photo || null);
      } else {
        // Use JSON when no image file is selected
        const res = await axiosInstance.put(API_ENDPOINTS.auth.profile, {
          name: profileData.name,
          mobile_number: profileData.mobile_number,
          profile: {
            personal_email: profileData.profile.personal_email,
            gender: profileData.profile.gender,
            dob: profileData.profile.dob || null,
            marital_status: profileData.profile.marital_status,
            blood_group: profileData.profile.blood_group,
          },
        });
        setProfilePhoto(res.data?.profile?.profile_photo || null);
      }

      setProfileImageFile(null);
      setIsEditing(false);
      showToast('Profile saved successfully!', 'success');
    } catch {
      showToast('Failed to save profile', 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  // Address state
  interface AddressData {
    id?: number;
    address_type: number | '';
    address_line1: string;
    address_line2: string;
    landmark: string;
    city: number | '';
    state: number | '';
    pincode: string;
    is_default: boolean;
  }

// Server resolves State/City ids, so no client-side matching.
  interface ReverseGeocodeResponse {
    formatted_address: string;
    address_line1: string;
    address_line2: string;
    city: string;
    city_id: number | null;
    city_matched: boolean;
    state: string;
    state_id: number | null;
    pincode: string;
    latitude: number;
    longitude: number;
    source: string;
    precision: string;
  }
  const emptyAddress: AddressData = {
    address_type: '', address_line1: '', address_line2: '', landmark: '', city: '', state: '', pincode: '', is_default: false,
  };
  const [addressTypes, setAddressTypes] = useState<{ id: number; name: string }[]>([]);
  const [addresses, setAddresses] = useState<(AddressData & { id: number })[]>([]);
  const [addressForm, setAddressForm] = useState<AddressData>(emptyAddress);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [statesList, setStatesList] = useState<{ id: number; name: string }[]>([]);
  const [citiesList, setCitiesList] = useState<{ id: number; name: string }[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [cityNameById, setCityNameById] = useState<Record<number, string>>({});
  const fetchedCityStateIdsRef = useRef<Set<number>>(new Set());
  const editAddressFormRef = useRef<HTMLDivElement>(null);
  const personalTabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeTab !== 'address') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.addresses.states, {
          params: { page_size: 500 },
        });
        const data = res.data?.results ?? res.data;
        if (!cancelled && Array.isArray(data)) setStatesList(data);
      } catch {
        // Selects fall back to the "unavailable" hint added below.
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);

  useEffect(() => {
    if (!addressForm.state) {
      setCitiesList([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setCitiesLoading(true);
        const res = await axiosInstance.get(API_ENDPOINTS.addresses.cities, {
          params: { state_id: addressForm.state },
        });
        const data = res.data?.results ?? res.data;
        if (!cancelled && Array.isArray(data)) {
          setCitiesList(data);
          setCityNameById(prev => {
            const next = { ...prev };
            data.forEach((c: { id: number; name: string }) => { next[c.id] = c.name; });
            return next;
          });
        }
      } catch {
        if (!cancelled) setCitiesList([]);
      } finally {
        if (!cancelled) setCitiesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [addressForm.state]);

  // Resolve city names for already-saved addresses by fetching cities of each referenced state
  useEffect(() => {
    const stateIds = Array.from(new Set(
      addresses
        .map(a => a.state)
        .filter((s): s is number => typeof s === 'number' && !fetchedCityStateIdsRef.current.has(s))
    ));
    if (stateIds.length === 0) return;
    stateIds.forEach(id => fetchedCityStateIdsRef.current.add(id));
    let cancelled = false;
    (async () => {
      const results = await Promise.all(stateIds.map(async id => {
        try {
          const res = await axiosInstance.get(API_ENDPOINTS.addresses.cities, { params: { state_id: id } });
          const data = res.data?.results ?? res.data;
          return Array.isArray(data) ? data as { id: number; name: string }[] : [];
        } catch {
          fetchedCityStateIdsRef.current.delete(id);
          return [];
        }
      }));
      if (cancelled) return;
      setCityNameById(prev => {
        const next = { ...prev };
        results.flat().forEach(c => { next[c.id] = c.name; });
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [addresses]);

  const stateNameById = React.useMemo(() => {
    const map: Record<number, string> = {};
    statesList.forEach(s => { map[s.id] = s.name; });
    return map;
  }, [statesList]);

  const fetchAddresses = useCallback(async () => {
    try {
      setAddressLoading(true);
      const [addrRes, typesRes] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.addresses.self),
        axiosInstance.get(API_ENDPOINTS.addresses.types),
      ]);
      setAddresses(addrRes.data?.results || addrRes.data || []);
      setAddressTypes(typesRes.data?.results || typesRes.data || []);
    } catch {
      showToast('Failed to load addresses', 'error');
    } finally {
      setAddressLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (activeTab === 'address') fetchAddresses();
  }, [activeTab, fetchAddresses]);

  const handleAddressFormChange = (field: keyof AddressData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setAddressForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleEditAddress = (addr: AddressData & { id: number }) => {
    setEditingAddressId(addr.id);
    setAddressForm({
      id: addr.id,
      address_type: addr.address_type,
      address_line1: addr.address_line1 || '',
      address_line2: addr.address_line2 || '',
      landmark: addr.landmark || '',
      city: typeof addr.city === 'number' ? addr.city : '',
      state: typeof addr.state === 'number' ? addr.state : '',
      pincode: addr.pincode || '',
      is_default: !!addr.is_default,
    });
    requestAnimationFrame(() => {
      editAddressFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const persistAddress = async (markAsDefault: boolean) => {
    try {
      setAddressSaving(true);
      const payload = {
        address_type: Number(addressForm.address_type),
        address_line1: addressForm.address_line1,
        address_line2: addressForm.address_line2,
        landmark: addressForm.landmark,
        city: addressForm.city === '' ? null : Number(addressForm.city),
        state: addressForm.state === '' ? null : Number(addressForm.state),
        pincode: addressForm.pincode,
        is_default: markAsDefault,
      };
      const existingDefault = addresses.find(a => a.is_default && a.id !== editingAddressId);
      if (editingAddressId) {
        await axiosInstance.patch(API_ENDPOINTS.addresses.selfDetail(editingAddressId), payload);
        showToast('Address updated successfully!', 'success');
      } else {
        await axiosInstance.post(API_ENDPOINTS.addresses.self, payload);
        showToast('Address added successfully!', 'success');
      }
      if (markAsDefault && existingDefault) {
        try {
          await axiosInstance.patch(API_ENDPOINTS.addresses.selfDetail(existingDefault.id), { is_default: false });
        } catch {
          // backend may already unset previous default automatically; ignore failure here
        }
      }
      setAddressForm(emptyAddress);
      setEditingAddressId(null);
      fetchAddresses();
      fetchSummaryCounts();
    } catch {
      showToast('Failed to save address', 'error');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!addressForm.address_type || !addressForm.address_line1.trim() || !addressForm.pincode.trim()) {
      showToast('Address type, address line 1, and PIN code are required', 'error');
      return;
    }

    const wantsDefault = !!addressForm.is_default;
    const existingDefault = addresses.find(a => a.is_default && a.id !== editingAddressId);

    if (wantsDefault && existingDefault) {
      showConfirm({
        title: 'Change Default Address?',
        message: 'Do you want to change your default address to this address? Your previous default will no longer be marked as default.',
        confirmLabel: 'Yes, change it',
        onConfirm: () => {
          setConfirmDialog(null);
          persistAddress(true);
        },
      });
      return;
    }

    persistAddress(wantsDefault);
  };

  const handleDeleteAddress = (addr: { id: number }) => {
    showConfirm({
      title: 'Delete Address',
      message: 'Are you sure you want to delete this address?',
      confirmLabel: 'Delete',
      confirmColor: '#f43f5e',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await axiosInstance.delete(API_ENDPOINTS.addresses.selfDetail(addr.id));
          showToast('Address deleted', 'success');
          fetchAddresses();
          fetchSummaryCounts();
        } catch {
          showToast('Failed to delete address', 'error');
        }
      },
    });
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      showToast('Geolocation is not supported by your browser', 'error');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const res = await axiosInstance.post<ReverseGeocodeResponse>(
            API_ENDPOINTS.addresses.reverseGeocode,
            {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
          );
          const data = res.data;

          // Null ids keep the user's existing selection instead of clearing it.
          setAddressForm(prev => ({
            ...prev,
            address_line1: data.address_line1 || prev.address_line1,
            address_line2: data.address_line2 || prev.address_line2,
            state: data.state_id != null ? data.state_id : prev.state,
            city: data.city_id != null ? data.city_id : prev.city,
            pincode: data.pincode || prev.pincode,
          }));

          if (data.precision === 'city') {
            showToast('Location is approximate — please check the address', 'info');
          } else if (!data.city_matched) {
            showToast('Address found — please select your city', 'info');
          } else {
            showToast('Location fetched successfully', 'success');
          }
        } catch (error) {
          const status = (error as AxiosError)?.response?.status;
          if (status === 502) {
            showToast('Location service unavailable. Please enter your address manually.', 'error');
          } else if (status === 429) {
            showToast('Too many requests. Please try again in a moment.', 'error');
          } else {
            showToast('Failed to fetch address from location', 'error');
          }
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        setLocationLoading(false);
        if (error.code === error.PERMISSION_DENIED) {
          showToast('Location permission denied.', 'error');
        } else if (error.code === error.TIMEOUT) {
          showToast('Timed out while getting your location. Please try again.', 'error');
        } else {
          showToast('Could not determine your location. Please try again.', 'error');
        }
      },
      // A network-derived fix can be kilometres off, which gives wrong PIN codes.
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };


  // Change password state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '', new_password: '', confirm_password: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handlePasswordChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      showToast('All password fields are required', 'error');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }
    try {
      setPasswordSaving(true);
      await axiosInstance.post(API_ENDPOINTS.auth.changePassword, passwordForm);
      showToast('Password changed successfully!', 'success');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err: any) {
      const msg =
        err.response?.data?.current_password?.[0] ||
        err.response?.data?.new_password?.[0] ||
        err.response?.data?.confirm_password?.[0] ||
        err.response?.data?.detail ||
        'Failed to change password';
      showToast(msg, 'error');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Family Members state
  const [familyMembers, setFamilyMembers] = useState<Dependant[]>([]);
  const [relationshipTypes, setRelationshipTypes] = useState<RelationshipType[]>([]);
  const [loadingFamily, setLoadingFamily] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<Dependant | null>(null);
  const [familyFormData, setFamilyFormData] = useState<DependantFormData>({
    name: '', relationship: '', gender: '', email: '', mobile_number: '', dob: '',
  });
  const [familySubmitting, setFamilySubmitting] = useState(false);
  const [togglingLogin, setTogglingLogin] = useState<number | null>(null);

  const fetchFamilyMembers = useCallback(async () => {
    setLoadingFamily(true);
    try {
      const [membersRes, typesRes] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.dependants.list),
        axiosInstance.get(API_ENDPOINTS.dependants.relationshipTypes),
      ]);
      setFamilyMembers(membersRes.data?.results || membersRes.data || []);
      setRelationshipTypes(typesRes.data?.results || typesRes.data || []);
    } catch {
      showToast('Failed to load family members', 'error');
    } finally {
      setLoadingFamily(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (activeTab === 'family') {
      fetchFamilyMembers();
    }
  }, [activeTab, fetchFamilyMembers]);

  const resetFamilyForm = () => {
    setFamilyFormData({ name: '', relationship: '', gender: '', email: '', mobile_number: '', dob: '' });
    setShowAddForm(false);
    setEditingMember(null);
  };

  const handleOpenEdit = (member: Dependant) => {
    setEditingMember(member);
    setFamilyFormData({
      name: member.name,
      relationship: member.relationship || '',
      gender: (() => { const g = (member.gender || '').trim().toLowerCase(); if (g === 'm' || g === 'male') return 'male'; if (g === 'f' || g === 'female') return 'female'; if (g === 'o' || g === 'other') return 'other'; return ''; })(),
      email: member.email || '',
      mobile_number: member.mobile_number || '',
      dob: member.dob || '',
    });
    setShowAddForm(true);
  };

  const handleFamilySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!familyFormData.name || !familyFormData.relationship) {
      showToast('Name and relationship are required', 'error');
      return;
    }
    setFamilySubmitting(true);
    try {
      const payload = { ...familyFormData, relationship: Number(familyFormData.relationship) };
      if (editingMember) {
        await axiosInstance.patch(API_ENDPOINTS.dependants.detail(editingMember.id), payload);
        showToast('Family member updated', 'success');
      } else {
        await axiosInstance.post(API_ENDPOINTS.dependants.list, payload);
        showToast('Family member added', 'success');
      }
      resetFamilyForm();
      fetchFamilyMembers();
      fetchSummaryCounts();
    } catch {
      showToast('Failed to save family member', 'error');
    } finally {
      setFamilySubmitting(false);
    }
  };

  const handleDeleteMember = (member: Dependant) => {
    showConfirm({
      title: 'Remove Family Member',
      message: `Are you sure you want to remove ${member.name}?${member.login_enabled ? ' Their login access will also be revoked.' : ''}`,
      confirmLabel: 'Remove',
      confirmColor: '#f43f5e',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await axiosInstance.delete(API_ENDPOINTS.dependants.detail(member.id));
          showToast('Family member removed', 'success');
          fetchFamilyMembers();
          fetchSummaryCounts();
          await fetchProfiles();
          if (activeProfile && String(activeProfile.id) === String(member.id)) {
            switchToSelf();
          }
        } catch {
          showToast('Failed to remove family member', 'error');
        }
      },
    });
  };

  const handleToggleLogin = (member: Dependant) => {
    const enabling = !member.login_enabled;
    if (enabling && !member.email) {
      showToast('Email is required to enable login access', 'error');
      return;
    }
    if (enabling) {
      // If not yet invited, use invite endpoint; if already invited, use toggle-login
      const isFirstInvite = !member.invite_status || member.invite_status === 'not_invited';
      showConfirm({
        title: 'Enable Login Access',
        message: `An invite email will be sent to ${member.email}. ${member.name} can use it to set up their account and log in independently.`,
        confirmLabel: 'Enable & Send Invite',
        onConfirm: async () => {
          setConfirmDialog(null);
          setTogglingLogin(member.id);
          try {
            let res;
            if (isFirstInvite) {
              res = await axiosInstance.post(API_ENDPOINTS.dependants.invite(member.id));
            } else {
              res = await axiosInstance.post(API_ENDPOINTS.dependants.toggleLogin(member.id), { enable: true });
            }
            const data = res?.data;
            if (data?.setup_link) {
              // Show dialog with setup link and username if email wasn't sent
              setConfirmDialog({
                title: 'Account Created',
                message: `Username: ${data.username}\n\nSetup Link: ${data.setup_link}\n\nShare this link with ${member.name} to set up their password.`,
                confirmLabel: 'Copy Link',
                confirmColor: '#06b6d4',
                onConfirm: () => {
                  navigator.clipboard.writeText(data.setup_link);
                  showToast('Setup link copied to clipboard', 'success');
                  setConfirmDialog(null);
                },
              });
            } else {
              showToast(data?.message || `Invite sent to ${member.email}`, 'success');
            }
            fetchFamilyMembers();
            await fetchProfiles();
          } catch {
            showToast('Failed to enable login access', 'error');
          } finally {
            setTogglingLogin(null);
          }
        },
      });
    } else {
      const doDisable = async () => {
        setTogglingLogin(member.id);
        try {
          await axiosInstance.post(API_ENDPOINTS.dependants.toggleLogin(member.id), { enable: false });
          showToast(`Login disabled for ${member.name}`, 'success');
          fetchFamilyMembers();
          await fetchProfiles();
          if (activeProfile && String(activeProfile.id) === String(member.id)) {
            switchToSelf();
          }
        } catch {
          showToast('Failed to disable login access', 'error');
        } finally {
          setTogglingLogin(null);
        }
      };
      doDisable();
    }
  };

  const handleResendInvite = async (member: Dependant) => {
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.dependants.resendInvite(member.id));
      const data = res?.data;
      if (data?.setup_link) {
        setConfirmDialog({
          title: 'Invite Resent',
          message: `Username: ${data.username}\n\nSetup Link:\n${data.setup_link}\n\nShare this link with ${member.name} to set up their password.`,
          confirmLabel: 'Copy Link',
          confirmColor: '#06b6d4',
          onConfirm: () => {
            navigator.clipboard.writeText(data.setup_link);
            showToast('Setup link copied to clipboard', 'success');
            setConfirmDialog(null);
          },
        });
      } else {
        showToast(data?.message || `Invite resent to ${member.email}`, 'success');
      }
    } catch {
      showToast('Failed to resend invite', 'error');
    }
  };

  // Family Data Sharing (Privacy tab)
  const [familyLink, setFamilyLink] = useState<{
    primary_user_name: string;
    relationship_name: string;
    share_data_with_primary: boolean;
  } | null>(null);
  const [loadingFamilyLink, setLoadingFamilyLink] = useState(false);
  const [togglingShare, setTogglingShare] = useState(false);

  // Consent preferences (Privacy tab)
  interface ConsentMap {
    share_with_doctors: boolean;
    share_with_corporate: boolean;
    analytics: boolean;
    email_notifications: boolean;
    sms_notifications: boolean;
    push_notifications: boolean;
    marketing_email: boolean;
  }
  const defaultConsents: ConsentMap = {
    share_with_doctors: true,
    share_with_corporate: false,
    analytics: true,
    email_notifications: true,
    sms_notifications: true,
    push_notifications: true,
    marketing_email: false,
  };
  const [consents, setConsents] = useState<ConsentMap>(defaultConsents);
  const [consentsLoading, setConsentsLoading] = useState(false);
  const [consentsSaving, setConsentsSaving] = useState(false);
  const [deletionPassword, setDeletionPassword] = useState('');
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);

  const fetchConsents = useCallback(async () => {
    setConsentsLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.auth.consents);
      const fetched: ConsentMap = { ...defaultConsents };
      (res.data as { purpose: string; granted: boolean }[]).forEach((c) => {
        if (c.purpose in fetched) {
          (fetched as unknown as Record<string, boolean>)[c.purpose] = c.granted;
        }
      });
      setConsents(fetched);
    } catch {
      // Use defaults if fetch fails
    } finally {
      setConsentsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConsentToggle = (purpose: keyof ConsentMap) => {
    setConsents(prev => ({ ...prev, [purpose]: !prev[purpose] }));
  };

  const handleSaveConsents = async () => {
    setConsentsSaving(true);
    try {
      const promises = Object.entries(consents).map(([purpose, granted]) =>
        axiosInstance.post(API_ENDPOINTS.auth.consents, { purpose, granted })
      );
      await Promise.all(promises);
      showToast('Privacy settings saved!', 'success');
    } catch {
      showToast('Failed to save privacy settings', 'error');
    } finally {
      setConsentsSaving(false);
    }
  };

  const handleRequestExport = async () => {
    setExportLoading(true);
    try {
      await axiosInstance.post(API_ENDPOINTS.auth.dataExport);
      showToast('Data export request submitted. You will receive a download link via email.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to request data export';
      showToast(msg, 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!deletionPassword) {
      showToast('Password is required to request account deletion', 'error');
      return;
    }
    setDeletionLoading(true);
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.auth.accountDeletion, {
        password: deletionPassword,
      });
      showToast(`Account deletion scheduled for ${new Date(res.data.scheduled_deletion_at).toLocaleDateString()}. You have 30 days to cancel.`, 'info');
      setShowDeletionDialog(false);
      setDeletionPassword('');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to request account deletion';
      showToast(msg, 'error');
    } finally {
      setDeletionLoading(false);
    }
  };

  const fetchFamilyLink = useCallback(async () => {
    setLoadingFamilyLink(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.dependants.myFamilyLink);
      setFamilyLink(res.data);
    } catch {
      // Not a family member - no link exists, that's ok
      setFamilyLink(null);
    } finally {
      setLoadingFamilyLink(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'privacy') {
      fetchFamilyLink();
      fetchConsents();
    }
  }, [activeTab, fetchFamilyLink, fetchConsents]);

  const handleToggleShare = async () => {
    if (!familyLink) return;
    setTogglingShare(true);
    try {
      const res = await axiosInstance.patch(API_ENDPOINTS.dependants.myPrivacy, {
        share_data_with_primary: !familyLink.share_data_with_primary,
      });
      setFamilyLink(prev => prev ? { ...prev, share_data_with_primary: res.data.share_data_with_primary } : null);
      showToast(res.data.share_data_with_primary ? 'Data sharing enabled' : 'Data sharing disabled', 'success');
    } catch {
      showToast('Failed to update privacy setting', 'error');
    } finally {
      setTogglingShare(false);
    }
  };

  const tabs: { key: ProfileTab; label: string; icon: IconDefinition }[] = [
    { key: 'personal', label: 'Personal Info', icon: faUser },
    { key: 'family', label: 'Family Members', icon: faUsers },
    { key: 'address', label: 'Addresses', icon: faMapMarkerAlt },
    { key: 'security', label: 'Security', icon: faLock },
    { key: 'language', label: t('common:language.switchLanguage'), icon: faGlobe },
  ];

  const stats: StatItem[] = [
    { icon: faUsers, color: '#06b6d4', value: summaryCounts.family_members_count, label: 'Family Members' },
    { icon: faMapMarkerAlt, color: '#10b981', value: summaryCounts.addresses_count, label: 'Addresses' },
    { icon: faBell, color: '#f59e0b', value: summaryCounts.reminders_count, label: 'Reminders' },
  ];

  return (
    <div className="pf-page">
      {/* Toast Notification */}
      {toast && (
        <div className="pf-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          confirmColor={confirmDialog.confirmColor}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* ============ WELCOME BANNER ============ */}
      <section className="pf-welcome-banner">
        <Container>
          <BackButton />
          <div className="pf-welcome-layout">
            <div className="pf-welcome-left">
              <div
                className="pf-welcome-avatar"
                onClick={handleAvatarClick}
                style={{
                  backgroundImage: profileImage ? `url(${profileImage})` : 'none',
                }}
              >
                {!profileImage && (
                  <FontAwesomeIcon icon={faUser} className="pf-welcome-avatar-icon" />
                )}
                <div className="pf-welcome-avatar-overlay">
                  <FontAwesomeIcon icon={faCamera} />
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <div className="pf-welcome-info">
                <h1 className="pf-welcome-title">{profileData.name || user?.name || 'User'}</h1>
                <p className="pf-welcome-subtitle">Member ID: {profileData.member_id || user?.member_id || '\u2014'}</p>
                <div className="pf-welcome-badges">
                  <span className="pf-welcome-badge">
                    <FontAwesomeIcon icon={faIdCard} /> {profileData.member_id || user?.member_id || '\u2014'}
                  </span>
                  <span className="pf-welcome-badge pf-welcome-badge-active">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Active Member
                  </span>
                </div>
              </div>
            </div>
            <div className="pf-welcome-actions">
              {activeTab === 'personal' && (
                <button className="pf-welcome-btn primary" onClick={() => {
                  const next = !isEditing;
                  setIsEditing(next);
                  if (next) {
                    requestAnimationFrame(() => {
                      personalTabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                  }
                }}>
                  <FontAwesomeIcon icon={faPencilAlt} />
                  {isEditing ? 'Cancel Edit' : 'Edit Profile'}
                </button>
              )}
              {activeTab === 'family' && (
                <button className="pf-welcome-btn primary" onClick={() => { resetFamilyForm(); setShowAddForm(true); }}>
                  <FontAwesomeIcon icon={faUserPlus} />
                  Add Member
                </button>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* ============ STATS ROW ============ */}
      <section className="pf-stats-section">
        <Container>
          <div className="pf-stats-row">
            {stats.map((item, idx) => (
              <div className="pf-stat-card" key={idx}>
                <div className="pf-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="pf-stat-info">
                  <span className="pf-stat-value">{item.value}</span>
                  <span className="pf-stat-label">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ============ CONTENT SECTION ============ */}
      <section className="pf-content-section">
        <Container>
          {/* Tab Navigation */}
          <div className="pf-tabs-container">
            {tabs.map(tab => (
              <button
                key={tab.key}
                className={`pf-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => tab.key === 'privacy' ? navigate('/privacy') : setActiveTab(tab.key)}
              >
                <span className="pf-tab-icon">
                  <FontAwesomeIcon icon={tab.icon} />
                </span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ─── Personal Info Tab ─── */}
          {activeTab === 'personal' && (
            <div className="pf-tab-content" ref={personalTabRef}>
              <div className="pf-section-header">
                <h2 className="pf-section-title">
                  <FontAwesomeIcon icon={faIdCard} className="pf-section-title-icon" />
                  Personal Information
                </h2>
                <p className="pf-section-subtitle">
                  Keep your personal details up to date. This information helps us serve you better and ensures accurate health records.
                </p>
              </div>

              <div className="pf-form-card">
                <h3 className="pf-form-card-title">
                  <FontAwesomeIcon icon={faUser} className="pf-form-card-title-icon" />
                  Basic Details
                </h3>
                <form className="pf-form">
                  <div className="pf-form-row">
                    <div className="pf-form-group">
                      <label>Member ID</label>
                      <input type="text" value={profileData.member_id} readOnly className="pf-readonly" />
                    </div>
                    <div className="pf-form-group">
                      <label>Full Name <span className="pf-required">*</span></label>
                      <input type="text" placeholder="Enter your full name" value={profileData.name} onChange={handleProfileChange('name')} disabled={!isEditing} className={!isEditing ? 'pf-readonly' : ''} />
                    </div>
                    <div className="pf-form-group">
                      <label>Employee ID</label>
                      <input type="text" value={profileData.employee_id} readOnly className="pf-readonly" />
                    </div>
                  </div>
                  <div className="pf-form-row">
                    <div className="pf-form-group">
                      <label>Official Email</label>
                      <input type="email" value={profileData.email} readOnly className="pf-readonly" placeholder="Not available" title="Official / work email provided by your organization" />
                    </div>
                    <div className="pf-form-group">
                      <label>Mobile Number <span className="pf-required">*</span></label>
                      <input type="tel" placeholder="Enter mobile number" value={profileData.mobile_number} onChange={handleProfileChange('mobile_number')} disabled={!isEditing} className={!isEditing ? 'pf-readonly' : ''} />
                    </div>
                    <div className="pf-form-group">
                      <label>Date of Birth</label>
                      <input type="date" value={profileData.profile.dob} onChange={handleProfileNestedChange('dob')} disabled={!isEditing} className={!isEditing ? 'pf-readonly' : ''} />
                    </div>
                  </div>
                  <div className="pf-form-row">
                    <div className="pf-form-group">
                      <label>Personal Email</label>
                      <input type="email" placeholder="Enter a personal email address (e.g. Gmail)" value={profileData.profile.personal_email} onChange={handleProfileNestedChange('personal_email')} disabled={!isEditing} className={!isEditing ? 'pf-readonly' : ''} />
                      <small className="pf-fields-hint">Used for personal communication and notifications.</small>
                    </div>
                  </div>
                </form>
              </div>

              <div className="pf-form-card">
                <h3 className="pf-form-card-title">
                  <FontAwesomeIcon icon={faShieldHalved} className="pf-form-card-title-icon" />
                  Additional Details
                </h3>
                <form className="pf-form">
                  <div className="pf-form-row">
                    <div className="pf-form-group">
                      <label>Gender</label>
                      <select value={profileData.profile.gender} onChange={handleProfileNestedChange('gender')} disabled={!isEditing} className={!isEditing ? 'pf-readonly' : ''}>
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="pf-form-group">
                      <label>Blood Group</label>
                      <select value={profileData.profile.blood_group} onChange={handleProfileNestedChange('blood_group')} disabled={!isEditing} className={!isEditing ? 'pf-readonly' : ''}>
                        <option value="">Select blood group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                    <div className="pf-form-group">
                      <label>Marital Status</label>
                      <select value={profileData.profile.marital_status} onChange={handleProfileNestedChange('marital_status')} disabled={!isEditing} className={!isEditing ? 'pf-readonly' : ''}>
                        <option value="">Select status</option>
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                      </select>
                    </div>
                  </div>
                </form>
              </div>

              {/* ─── Integrations ─── */}
              <div className="pf-form-card">
                <h3 className="pf-form-card-title">
                  <FontAwesomeIcon icon={faLink} className="pf-form-card-title-icon" />
                  Integrations
                </h3>
                <div className="pf-integrations-grid">
                  <div className="pf-integration-card">
                    <div className="pf-integration-icon" style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }}>
                      <FontAwesomeIcon icon={faIdCard} />
                    </div>
                    <div className="pf-integration-info">
                      <span className="pf-integration-name">ABHA (Ayushman Bharat)</span>
                      <span className="pf-integration-desc">Link your ABHA health ID for seamless health data access</span>
                    </div>
                    <button className="pf-btn pf-btn-outline" onClick={() => showToast('ABHA integration coming soon!', 'info')}>
                      Connect
                    </button>
                  </div>
                  <div className="pf-integration-card">
                    <div className="pf-integration-icon" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                      <FontAwesomeIcon icon={faFolderOpen} />
                    </div>
                    <div className="pf-integration-info">
                      <span className="pf-integration-name">DigiLocker</span>
                      <span className="pf-integration-desc">Access your official health documents from DigiLocker</span>
                    </div>
                    <button className="pf-btn pf-btn-outline" onClick={() => showToast('DigiLocker integration coming soon!', 'info')}>
                      Connect
                    </button>
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="pf-form-buttons">
                  <button className="pf-btn pf-btn-cancel" onClick={() => { setIsEditing(false); fetchProfile(); }}>Cancel</button>
                  <button className="pf-btn pf-btn-save" onClick={handleSaveProfile} disabled={profileSaving}>
                    {profileSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ─── Family Members Tab ─── */}
          {activeTab === 'family' && (
            <div className="pf-tab-content">
              <div className="pf-section-header">
                <h2 className="pf-section-title">
                  <FontAwesomeIcon icon={faUsers} className="pf-section-title-icon" />
                  Family Members
                </h2>
                <p className="pf-section-subtitle">
                  Add and manage your family members. You can also enable login access so they can use the platform independently.
                </p>
              </div>

              {/* Add/Edit Form */}
              {showAddForm && (
                <div className="pf-form-card">
                  <h3 className="pf-form-card-title">
                    <FontAwesomeIcon icon={editingMember ? faPencilAlt : faUserPlus} className="pf-form-card-title-icon" />
                    {editingMember ? 'Edit Family Member' : 'Add Family Member'}
                  </h3>
                  <form className="pf-form" onSubmit={handleFamilySubmit}>
                    <div className="pf-form-row">
                      <div className="pf-form-group">
                        <label>Full Name <span className="pf-required">*</span></label>
                        <input
                          type="text"
                          placeholder="Enter full name"
                          value={familyFormData.name}
                          onChange={e => setFamilyFormData(p => ({ ...p, name: e.target.value }))}
                        />
                      </div>
                      <div className="pf-form-group">
                        <label>Relationship <span className="pf-required">*</span></label>
                        <select
                          value={familyFormData.relationship}
                          onChange={e => setFamilyFormData(p => ({ ...p, relationship: e.target.value ? Number(e.target.value) : '' }))}
                        >
                          <option value="" disabled>Select relationship</option>
                          {relationshipTypes.map(rt => (
                            <option key={rt.id} value={rt.id}>{rt.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="pf-form-group">
                        <label>Gender</label>
                        <select
                          value={familyFormData.gender}
                          onChange={e => setFamilyFormData(p => ({ ...p, gender: e.target.value }))}
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="pf-form-row">
                      <div className="pf-form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          placeholder="Enter email address"
                          value={familyFormData.email}
                          onChange={e => setFamilyFormData(p => ({ ...p, email: e.target.value }))}
                        />
                      </div>
                      <div className="pf-form-group">
                        <label>Mobile Number</label>
                        <input
                          type="tel"
                          placeholder="Enter mobile number"
                          value={familyFormData.mobile_number}
                          onChange={e => setFamilyFormData(p => ({ ...p, mobile_number: e.target.value }))}
                        />
                      </div>
                      <div className="pf-form-group">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          value={familyFormData.dob}
                          onChange={e => setFamilyFormData(p => ({ ...p, dob: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="pf-form-buttons" style={{ borderTop: 'none', marginTop: 8, paddingTop: 0 }}>
                      <button type="button" className="pf-btn pf-btn-cancel" onClick={resetFamilyForm}>Cancel</button>
                      <button type="submit" className="pf-btn pf-btn-save" disabled={familySubmitting}>
                        {familySubmitting ? 'Saving...' : editingMember ? 'Update Member' : 'Add Member'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Loading State */}
              {loadingFamily && (
                <div className="pf-family-loading">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="pf-family-card pf-family-skeleton">
                      <div className="pf-skeleton-circle" />
                      <div className="pf-skeleton-lines">
                        <div className="pf-skeleton-line" style={{ width: '60%' }} />
                        <div className="pf-skeleton-line" style={{ width: '40%' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!loadingFamily && familyMembers.length === 0 && !showAddForm && (
                <div className="pf-family-empty">
                  <span className="pf-family-empty-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </span>
                  <h3>No Family Members Yet</h3>
                  <p>Add your family members to manage their health records and enable login access.</p>
                  <button className="pf-btn pf-btn-save" onClick={() => { resetFamilyForm(); setShowAddForm(true); }}>
                    <FontAwesomeIcon icon={faPlus} /> Add Your First Member
                  </button>
                </div>
              )}

              {/* Member Cards */}
              {!loadingFamily && familyMembers.length > 0 && (
                <div className="pf-family-grid">
                  {familyMembers.map(member => (
                    <div key={member.id} className="pf-family-card">
                      <div className="pf-family-card-header">
                        <div className="pf-family-avatar">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="pf-family-info">
                          <h4 className="pf-family-name">{member.name}</h4>
                          <span className="pf-family-relation">{member.relationship_name || 'Family Member'}</span>
                          {member.member_id && (
                            <span className="pf-family-member-id">
                              <FontAwesomeIcon icon={faIdCard} style={{ marginRight: 4, fontSize: '0.65rem' }} />
                              {member.member_id}
                            </span>
                          )}
                        </div>
                        <div className="pf-family-actions">
                          <button
                            className="pf-family-action-btn edit"
                            title="Edit"
                            onClick={() => handleOpenEdit(member)}
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            className="pf-family-action-btn delete"
                            title="Delete"
                            onClick={() => handleDeleteMember(member)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                      <div className="pf-family-details">
                        {member.email && (
                          <div className="pf-family-detail">
                            <span className="pf-family-detail-label">
                              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 4 }} /> Email
                            </span>
                            <span className="pf-family-detail-value">{member.email}</span>
                          </div>
                        )}
                        {member.mobile_number && (
                          <div className="pf-family-detail">
                            <span className="pf-family-detail-label">
                              <FontAwesomeIcon icon={faPhone} style={{ marginRight: 4 }} /> Phone
                            </span>
                            <span className="pf-family-detail-value">{member.mobile_number}</span>
                          </div>
                        )}
                        {member.dob && (
                          <div className="pf-family-detail">
                            <span className="pf-family-detail-label">
                              <FontAwesomeIcon icon={faCalendarDays} style={{ marginRight: 4 }} /> DOB
                            </span>
                            <span className="pf-family-detail-value">{new Date(member.dob).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                      <div className="pf-family-card-footer">
                        {!isDependantUser ? (
                          <>
                            <div className="pf-family-footer-top">
                              <span className="pf-family-login-label">Login Access</span>
                              <label className="pf-toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={!!member.login_enabled}
                                  onChange={() => handleToggleLogin(member)}
                                  disabled={togglingLogin === member.id}
                                />
                                <span className="pf-toggle-track">
                                  <span className="pf-toggle-knob" />
                                </span>
                                <span className="pf-toggle-text">
                                  {togglingLogin === member.id ? '...' : member.login_enabled ? 'On' : 'Off'}
                                </span>
                              </label>
                            </div>
                            {(member.invite_status || member.linked_user_username) && (
                              <div className="pf-family-footer-status">
                                {member.invite_status === 'invited' && (
                                  <>
                                    <span className="pf-family-invite-badge invited">Invite Pending</span>
                                    <button
                                      className="pf-family-resend-btn"
                                      onClick={() => handleResendInvite(member)}
                                    >
                                      <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 4 }} />
                                      Resend
                                    </button>
                                  </>
                                )}
                                {member.invite_status === 'active' && (
                                  <span className="pf-family-invite-badge active">Account Active</span>
                                )}
                                {member.linked_user_username && (
                                  <span className="pf-family-username">
                                    <FontAwesomeIcon icon={faUser} style={{ marginRight: 4, fontSize: '0.65rem' }} />
                                    {member.linked_user_username}
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="pf-family-footer-top">
                            <span className="pf-family-login-label" style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                              <FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: 4 }} />
                              Login access can only be granted by the primary account holder
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── Addresses Tab ─── */}
          {activeTab === 'address' && (
            <div className="pf-tab-content">
              <div className="pf-section-header">
                <h2 className="pf-section-title">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="pf-section-title-icon" />
                  Address Information
                </h2>
                <p className="pf-section-subtitle">
                  Manage your home, office, and other addresses. These are used for deliveries, appointments, and emergency services.
                </p>
              </div>

              {/* Existing Addresses */}
              {addressLoading ? (
                <div className="pf-family-loading">
                  {[1, 2].map(i => (
                    <div key={i} className="pf-family-card pf-family-skeleton">
                      <div className="pf-skeleton-circle" />
                      <div className="pf-skeleton-lines">
                        <div className="pf-skeleton-line" style={{ width: '60%' }} />
                        <div className="pf-skeleton-line" style={{ width: '40%' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : addresses.length > 0 && (
                <div className="pf-family-grid" style={{ marginBottom: 24 }}>
                  {addresses.map(addr => (
                    <div key={addr.id} className="pf-family-card">
                      <div className="pf-family-card-header">
                        <div className="pf-family-avatar" style={{ background: '#06b6d412', color: '#06b6d4' }}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                        </div>
                        <div className="pf-family-info">
                          <h4 className="pf-family-name">
                            {addressTypes.find(t => t.id === addr.address_type)?.name || 'Address'}
                            {addr.is_default && (
                              <span className="pf-default-badge">
                                <FontAwesomeIcon icon={faCheckCircle} /> Default
                              </span>
                            )}
                          </h4>
                          <span className="pf-family-relation">
                            {typeof addr.city === 'number' ? (cityNameById[addr.city] || '') : (addr.city || '')}
                            {addr.state ? `, ${typeof addr.state === 'number' ? (stateNameById[addr.state] || '') : addr.state}` : ''}
                          </span>
                        </div>
                        <div className="pf-family-actions">
                          <button className="pf-family-action-btn edit" title="Edit" onClick={() => handleEditAddress(addr)}>
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button className="pf-family-action-btn delete" title="Delete" onClick={() => handleDeleteAddress(addr)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                      <div className="pf-family-details">
                        <div className="pf-family-detail">
                          <span className="pf-family-detail-label">
                            <FontAwesomeIcon icon={faLocationDot} style={{ marginRight: 4 }} /> Address
                          </span>
                          <span className="pf-family-detail-value">
                            {addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}
                          </span>
                        </div>
                        {addr.landmark && (
                          <div className="pf-family-detail">
                            <span className="pf-family-detail-label">Landmark</span>
                            <span className="pf-family-detail-value">{addr.landmark}</span>
                          </div>
                        )}
                        <div className="pf-family-detail">
                          <span className="pf-family-detail-label">PIN Code</span>
                          <span className="pf-family-detail-value">{addr.pincode}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Address Form */}
              <div className="pf-form-card" ref={editAddressFormRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 className="pf-form-card-title" style={{ marginBottom: 0 }}>
                    <FontAwesomeIcon icon={editingAddressId ? faPencilAlt : faPlus} className="pf-form-card-title-icon" />
                    {editingAddressId ? 'Edit Address' : 'Add New Address'}
                  </h3>
                  <button
                    type="button"
                    className="pf-btn pf-btn-outline"
                    onClick={handleGetCurrentLocation}
                    disabled={locationLoading}
                    style={{
                      padding: '0.3rem 0.6rem',
                      fontSize: '0.8rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      lineHeight: 1
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faLocationCrosshairs}
                      style={{
                        fontSize: '0.85rem',
                        verticalAlign: 'middle'
                      }}
                    />
                    <span>{locationLoading ? 'Fetching...' : 'Use My Location'}</span>
                  </button>
                </div>
                <form className="pf-form">
                  <div className="pf-form-row">
                    <div className="pf-form-group">
                      <label>Address Type <span className="pf-required">*</span></label>
                      <select value={addressForm.address_type} onChange={handleAddressFormChange('address_type')}>
                        <option value="">Select type</option>
                        {addressTypes.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="pf-form-row">
                    <div className="pf-form-group pf-full-width">
                      <label>Address Line 1 <span className="pf-required">*</span></label>
                      <input type="text" placeholder="House/Flat No., Building Name" value={addressForm.address_line1} onChange={handleAddressFormChange('address_line1')} />
                    </div>
                  </div>
                  <div className="pf-form-row">
                    <div className="pf-form-group pf-full-width">
                      <label>Address Line 2</label>
                      <input type="text" placeholder="Street, Area, Colony" value={addressForm.address_line2} onChange={handleAddressFormChange('address_line2')} />
                    </div>
                  </div>
                  <div className="pf-form-row">
                    <div className="pf-form-group">
                      <label>State</label>
                      <select
                        value={addressForm.state === '' ? '' : String(addressForm.state)}
                        onChange={e => setAddressForm(prev => ({
                          ...prev,
                          state: e.target.value === '' ? '' : Number(e.target.value),
                          city: '',
                        }))}
                        disabled={statesList.length === 0}
                      >
                        <option value="">
                          {statesList.length === 0 ? 'States unavailable — please contact support' : 'Select state'}
                        </option>
                        {statesList.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="pf-form-group">
                      <label>City</label>
                      <select
                        value={addressForm.city === '' ? '' : String(addressForm.city)}
                        onChange={e => setAddressForm(prev => ({
                          ...prev,
                          city: e.target.value === '' ? '' : Number(e.target.value),
                        }))}
                        disabled={!addressForm.state || citiesLoading}
                      >
                        <option value="">
                          {!addressForm.state ? 'Select state first' : citiesLoading ? 'Loading…' : 'Select city'}
                        </option>
                        {citiesList.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="pf-form-group">
                      <label>PIN Code <span className="pf-required">*</span></label>
                      <input type="text" placeholder="Enter PIN code" maxLength={6} value={addressForm.pincode} onChange={handleAddressFormChange('pincode')} />
                    </div>
                  </div>
                  <div className="pf-form-row pf-form-row-landmark">
                    <div className="pf-form-group">
                      <label>Landmark</label>
                      <input type="text" placeholder="Near landmark" value={addressForm.landmark} onChange={handleAddressFormChange('landmark')} />
                    </div>
                    <div className="pf-form-group pf-default-group">
                      <label className="pf-default-checkbox">
                        <input
                          type="checkbox"
                          checked={addressForm.is_default}
                          onChange={e => setAddressForm(prev => ({ ...prev, is_default: e.target.checked }))}
                        />
                        <span>Set this address as a default</span>
                      </label>
                    </div>
                  </div>
                </form>
              </div>

              <div className="pf-form-buttons">
                {editingAddressId && (
                  <button className="pf-btn pf-btn-cancel" onClick={() => { setAddressForm(emptyAddress); setEditingAddressId(null); }}>
                    Cancel Edit
                  </button>
                )}
                <button className="pf-btn pf-btn-save" onClick={handleSaveAddress} disabled={addressSaving}>
                  {addressSaving ? 'Saving...' : editingAddressId ? 'Update Address' : 'Save Address'}
                </button>
              </div>
            </div>
          )}

          {/* ─── Security Tab ─── */}
          {activeTab === 'security' && (
            <div className="pf-tab-content">
              <div className="pf-section-header">
                <h2 className="pf-section-title">
                  <FontAwesomeIcon icon={faShieldHalved} className="pf-section-title-icon" />
                  Security Settings
                </h2>
                <p className="pf-section-subtitle">
                  Keep your account secure by updating your password regularly and managing your login preferences.
                </p>
              </div>

              <div className="pf-form-card">
                <h3 className="pf-form-card-title">
                  <FontAwesomeIcon icon={faKey} className="pf-form-card-title-icon" />
                  Change Password
                </h3>
                <form className="pf-form" onSubmit={e => { e.preventDefault(); handleChangePassword(); }}>
                  <div className="pf-form-row">
                    <div className="pf-form-group">
                      <label>Current Password <span className="pf-required">*</span></label>
                      <input type="password" placeholder="Enter current password" value={passwordForm.current_password} onChange={handlePasswordChange('current_password')} />
                    </div>
                  </div>
                  <div className="pf-form-row">
                    <div className="pf-form-group">
                      <label>New Password <span className="pf-required">*</span></label>
                      <input type="password" placeholder="Enter new password" value={passwordForm.new_password} onChange={handlePasswordChange('new_password')} />
                    </div>
                    <div className="pf-form-group">
                      <label>Confirm Password <span className="pf-required">*</span></label>
                      <input type="password" placeholder="Re-enter new password" value={passwordForm.confirm_password} onChange={handlePasswordChange('confirm_password')} />
                    </div>
                  </div>
                  <div className="pf-form-buttons" style={{ borderTop: 'none', marginTop: 8, paddingTop: 0 }}>
                    <button type="button" className="pf-btn pf-btn-cancel" onClick={() => setPasswordForm({ current_password: '', new_password: '', confirm_password: '' })}>
                      Clear
                    </button>
                    <button type="submit" className="pf-btn pf-btn-save" disabled={passwordSaving}>
                      {passwordSaving ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </form>
                <div className="pf-password-tips">
                  <div className="pf-password-tips-title">
                    <FontAwesomeIcon icon={faShieldHalved} /> Password Tips
                  </div>
                  <ul className="pf-password-tips-list">
                    <li>Use at least 8 characters with a mix of uppercase, lowercase, numbers and symbols.</li>
                    <li>Avoid using personal information such as your name, date of birth or phone number.</li>
                    <li>Use a unique password you have not used for any other account.</li>
                    <li>Never share your password with anyone; we will never ask for it.</li>
                  </ul>
                </div>
              </div>

              <div className="pf-form-card">
                <h3 className="pf-form-card-title">
                  <FontAwesomeIcon icon={faShieldHalved} className="pf-form-card-title-icon" />
                  Login Preferences
                </h3>
                <div className="pf-security-options">
                  <div className="pf-security-option">
                    <div className="pf-security-option-info">
                      <span className="pf-security-option-title">Two-Factor Authentication</span>
                      <span className="pf-security-option-desc">Add an extra layer of security to your account</span>
                    </div>
                    <div className="pf-security-option-badge disabled">Disabled</div>
                  </div>
                  <div className="pf-security-option">
                    <div className="pf-security-option-info">
                      <span className="pf-security-option-title">Login Notifications</span>
                      <span className="pf-security-option-desc">Get notified when someone logs into your account</span>
                    </div>
                    <div className="pf-security-option-badge enabled">Enabled</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'language' && (
            <div className="pf-tab-content">
              <div className="pf-section-header">
                <h2 className="pf-section-title">
                  <FontAwesomeIcon icon={faGlobe} className="pf-section-title-icon" />
                  {t('common:language.switchLanguage')}
                </h2>
                <p className="pf-section-subtitle">
                  {i18n.language?.startsWith('hi')
                    ? 'अपनी पसंदीदा भाषा चुनें। ऐप आपकी चुनी हुई भाषा में प्रदर्शित होगा।'
                    : 'Choose your preferred language. The app will be displayed in your selected language.'}
                </p>
              </div>

              <div className="pf-form-card">
                <div className="pf-lang-grid">
                  {Object.entries(supportedLanguages).map(([code, lang]) => {
                    const currentLang = i18n.resolvedLanguage?.split('-')[0] || 'en';
                    const isActive = currentLang === code;
                    return (
                      <button
                        key={code}
                        className={`pf-lang-card${isActive ? ' active' : ''}`}
                        onClick={() => i18n.changeLanguage(code)}
                        type="button"
                      >
                        <span className="pf-lang-flag">{lang.flag}</span>
                        <span className="pf-lang-name">{lang.nativeLabel}</span>
                        <span className="pf-lang-label">{lang.label}</span>
                        {isActive && (
                          <span className="pf-lang-active-badge">
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Privacy tab redirects to /privacy page */}
        </Container>
      </section>
    </div>
  );
};

export default Profile;
